import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router';
import { object, string } from 'yup';
import {
  namedPartyFromSubscription,
  parseLicensesPageFromResponse,
  parseSubscriptionFromResponse,
  subscriptionsApi,
  type SubscriptionLicense,
} from 'src/api/subscriptionsApi';
import { Button, Tabs } from 'src/common';
import { RHFCustomSelect, RHFProvider, RHFTextField } from 'src/common/rhf';
import SubHeader from 'src/components/SubHeader';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { SubscriptionLicensesTable } from './create-form';

const DEFAULT_LICENSES_PAGE_SIZE = 25;

type OverviewFormValues = {
  subscriptionOrderNumber: string;
  customerReference: string;
  clientName: string;
  subscriptionType: string;
  internalNotes: string;
  items: Array<{ productName: string; licenses: string }>;
};

const TYPE_OPTIONS = [
  { id: 'monthly', label: 'Monthly', value: 'monthly' },
  { id: 'yearly', label: 'Yearly', value: 'yearly' },
];

const DETAIL_TABS = [
  { label: 'Subscription Overview', value: 'overview' },
  { label: 'Licenses', value: 'licenses' },
] as const;

type SubscriptionDetailsTab = (typeof DETAIL_TABS)[number]['value'];

const isSubscriptionDetailsTab = (value: string | undefined | null): value is SubscriptionDetailsTab =>
  value === 'overview' || value === 'licenses';

const DEFAULT_SUBSCRIPTION_DETAILS_TAB: SubscriptionDetailsTab = 'overview';

const getSubscriptionDetailsHref = (
  subscriptionId: string,
  tab: SubscriptionDetailsTab = DEFAULT_SUBSCRIPTION_DETAILS_TAB
) => `/subscriptions/${encodeURIComponent(subscriptionId)}/${tab}`;

const validationSchema = object({
  internalNotes: string().trim().optional(),
});

const formatDate = (value?: string | Date | null) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const sideCardStyle = (divider: string) => ({
  border: `1px solid ${divider}`,
  borderRadius: 8,
  padding: 20,
  backgroundColor: '#fff',
});

const createdByLabel = (value: unknown): string => {
  if (!value) return '—';
  if (typeof value === 'string') return value;
  const row = value as { name?: string; firstName?: string; lastName?: string; email?: string };
  const full = `${row?.firstName || ''} ${row?.lastName || ''}`.trim();
  return row?.name || full || row?.email || '—';
};

const SubscriptionDetails = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { subscriptionId = '', tab: tabParam } = useParams<{
    subscriptionId?: string;
    tab?: string;
  }>();
  const { userData } = useUserProfile();
  const orgId = String(userData?.organization?.id || '').trim();
  const isReseller =
    userData?.organization?.organizationTypeName === 'Reseller';
  const divider = theme.palette.divider;
  const readOnlyFieldSx = {
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: theme.palette.text.primary,
      color: theme.palette.text.primary,
      opacity: 1,
      cursor: 'default',
    },
    '& .MuiSelect-select.Mui-disabled': {
      WebkitTextFillColor: theme.palette.text.primary,
      color: theme.palette.text.primary,
      cursor: 'default',
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      backgroundColor: '#F3F4F6',
    },
    '& .MuiOutlinedInput-root.Mui-disabled:hover': {
      backgroundColor: '#F3F4F6',
    },
    '&.Mui-disabled': {
      opacity: 1,
      backgroundColor: '#F3F4F6',
    },
    '&.Mui-disabled:hover': {
      backgroundColor: '#F3F4F6',
    },
    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
  };
  const requestedTab = isSubscriptionDetailsTab(tabParam) ? tabParam : null;
  const activeTab: SubscriptionDetailsTab = requestedTab ?? DEFAULT_SUBSCRIPTION_DETAILS_TAB;
  const [loading, setLoading] = useState(true);
  const [licensesLoading, setLicensesLoading] = useState(false);
  const [licenses, setLicenses] = useState<SubscriptionLicense[]>([]);
  const [licensesPage, setLicensesPage] = useState(0);
  const [licensesPageSize, setLicensesPageSize] = useState(DEFAULT_LICENSES_PAGE_SIZE);
  const [licensesTotal, setLicensesTotal] = useState(0);
  const [productNames, setProductNames] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState({
    startDate: '',
    endDate: '',
    createdBy: '—',
  });
  const [overviewReady, setOverviewReady] = useState(false);
  const skipLicensesRefetchRef = useRef(true);

  const methods = useForm<OverviewFormValues>({
    defaultValues: {
      subscriptionOrderNumber: '',
      customerReference: '',
      clientName: '',
      subscriptionType: '',
      internalNotes: '',
      items: [],
    },
    resolver: yupResolver(validationSchema) as any,
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = methods;

  const watched = watch();

  useEffect(() => {
    if (!subscriptionId) return;
    if (tabParam === activeTab) return;
    navigate(getSubscriptionDetailsHref(subscriptionId, activeTab), { replace: true });
  }, [subscriptionId, tabParam, activeTab, navigate]);

  useEffect(() => {
    if (!subscriptionId) return undefined;
    let cancelled = false;

    const loadOverview = async () => {
      setLoading(true);
      setOverviewReady(false);
      setLicensesPage(0);
      setLicensesPageSize(DEFAULT_LICENSES_PAGE_SIZE);
      try {
        const response = await subscriptionsApi.getSubscriptionById(subscriptionId, {
          page: 1,
          size: DEFAULT_LICENSES_PAGE_SIZE,
        });
        if (cancelled) return;
        const record = parseSubscriptionFromResponse(response);
        if (!record?.id) {
          toast.error('Subscription not found');
          navigate('/subscriptions');
          return;
        }

        const products = Array.isArray(record.products) ? record.products : [];
        const names: Record<string, string> = {};
        const items = products.map((product: any) => {
          const productId = String(product?.productId || '').trim();
          const productName = String(
            product?.productName || product?.packageName || product?.name || productId,
          ).trim();
          if (productId) names[productId] = productName;
          return {
            productName: productName || '—',
            licenses: String(product?.numberOfLicenses ?? ''),
          };
        });

        const typeRaw = String(record.subscriptionType || '').toLowerCase();
        const subscriptionType = typeRaw === 'yearly' ? 'yearly' : 'monthly';

        const client = namedPartyFromSubscription(record.client, record.clientId);
        const clientName = client?.name || client?.id || '';

        if (
          !isReseller &&
          orgId &&
          client?.id &&
          String(client.id).trim() !== orgId
        ) {
          toast.error('You do not have access to this subscription');
          navigate('/subscriptions');
          return;
        }

        const licensesPageData = parseLicensesPageFromResponse(response);

        if (cancelled) return;

        reset({
          subscriptionOrderNumber: String(record.subscriptionOrderNumber || ''),
          customerReference: String(record.customerReference || ''),
          clientName: clientName || '—',
          subscriptionType,
          internalNotes: String(record.internalNotes || ''),
          items: items.length ? items : [{ productName: '—', licenses: '' }],
        });
        setProductNames(names);
        setLicenses(licensesPageData.items);
        setLicensesTotal(licensesPageData.totalRecords);
        if (licensesPageData.pageSize > 0) {
          setLicensesPageSize(licensesPageData.pageSize);
        }
        setPreview({
          startDate: String(record.startDate || ''),
          endDate: String(record.endDate || ''),
          createdBy: createdByLabel(record.createdBy),
        });
        skipLicensesRefetchRef.current = true;
        setOverviewReady(true);
      } catch (error: any) {
        if (cancelled) return;
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to load subscription',
        );
        navigate('/subscriptions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadOverview();
    return () => {
      cancelled = true;
    };
  }, [subscriptionId, navigate, reset, isReseller, orgId]);

  useEffect(() => {
    if (!subscriptionId || !overviewReady) return undefined;
    if (skipLicensesRefetchRef.current) {
      skipLicensesRefetchRef.current = false;
      return undefined;
    }

    let cancelled = false;
    const loadLicensesPage = async () => {
      setLicensesLoading(true);
      try {
        const response = await subscriptionsApi.getSubscriptionById(subscriptionId, {
          page: licensesPage + 1,
          size: licensesPageSize,
        });
        if (cancelled) return;
        const licensesPageData = parseLicensesPageFromResponse(response);
        setLicenses(licensesPageData.items);
        setLicensesTotal(licensesPageData.totalRecords);
      } catch (error: any) {
        if (cancelled) return;
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to load license keys',
        );
      } finally {
        if (!cancelled) setLicensesLoading(false);
      }
    };

    loadLicensesPage();
    return () => {
      cancelled = true;
    };
  }, [subscriptionId, overviewReady, licensesPage, licensesPageSize]);

  const previewProductLabel =
    (watched.items || [])
      .map((item) => item.productName)
      .filter((name) => name && name !== '—')
      .join(', ') || '—';
  const previewLicenses = (() => {
    const total = (watched.items || []).reduce(
      (sum, item) => sum + (Number(item.licenses) || 0),
      0,
    );
    return total > 0 ? String(total) : '—';
  })();

  const onSubmit = async (values: OverviewFormValues) => {
    if (!subscriptionId) return;
    try {
      await subscriptionsApi.updateSubscription(subscriptionId, {
        internalNotes: values.internalNotes?.trim() || undefined,
      });
      toast.success('Internal notes saved');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to save internal notes',
      );
    }
  };

  const handleBack = () => {
    navigate('/subscriptions');
  };

  return (
    <>
      <Helmet>
        <title>Mindtime User Portal | Subscription Details</title>
      </Helmet>
      <SubHeader title="Subscriptions" />
      <Box px="30px" pt={3} pb={4}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs
              tabs={[...DETAIL_TABS]}
              activeTab={activeTab}
              isTransform={false}
              handleTabChange={(_event, value) => {
                const nextTab = String(value ?? '');
                if (!subscriptionId || !isSubscriptionDetailsTab(nextTab)) return;
                navigate(getSubscriptionDetailsHref(subscriptionId, nextTab));
              }}
            />
            {activeTab === 'licenses' ? (
              <Paper
                elevation={0}
                style={{
                  border: `1px solid ${divider}`,
                  borderRadius: 8,
                  padding: 24,
                  backgroundColor: '#fff',
                }}>
                <Typography
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    marginBottom: 16,
                    color: theme.palette.text.primary,
                  }}>
                  License keys
                </Typography>
                <SubscriptionLicensesTable
                  licenses={licenses}
                  productNames={productNames}
                  page={licensesPage}
                  pageSize={licensesPageSize}
                  totalRecords={licensesTotal}
                  loading={licensesLoading}
                  onPageChange={setLicensesPage}
                  onPageSizeChange={(size) => {
                    setLicensesPageSize(size);
                    setLicensesPage(0);
                  }}
                />
                <Box display="flex" justifyContent="flex-end" style={{ marginTop: 24 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    color="inherit"
                    onClick={handleBack}
                    sx={{ minWidth: 120, height: 42 }}>
                    Back
                      </Button>
                </Box>
              </Paper>
            ) : (
              <RHFProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Box
                  display="grid"
                  gridTemplateColumns="minmax(0, 2fr) minmax(280px, 1fr)"
                  style={{ gap: 24, alignItems: 'start' }}>
                  <Paper
                    elevation={0}
                    style={{
                      border: `1px solid ${divider}`,
                      borderRadius: 8,
                      padding: 24,
                      backgroundColor: '#fff',
                    }}>
                    <Typography
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        marginBottom: 8,
                        color: theme.palette.text.primary,
                      }}>
                      Subscription Overview
                    </Typography>
                    <Typography
                      style={{
                        fontSize: 13,
                        color: theme.palette.text.secondary,
                        marginBottom: 20,
                      }}>
                      Only Internal Notes can be edited.
                    </Typography>

                    <Box
                      display="grid"
                      gridTemplateColumns="1fr 1fr 40px"
                      style={{ gap: 16, alignItems: 'start' }}>
                      <RHFTextField
                        name="subscriptionOrderNumber"
                        outerLabel="Subscription #"
                        disabled
                        readOnly
                        sx={readOnlyFieldSx}
                      />
                      <RHFTextField
                        name="customerReference"
                        outerLabel="Customer Reference"
                        disabled
                        readOnly
                        sx={readOnlyFieldSx}
                      />
                      <Box />
                      <RHFTextField
                        name="clientName"
                        outerLabel="Client"
                        disabled
                        readOnly
                        sx={readOnlyFieldSx}
                      />
                      <RHFCustomSelect
                        name="subscriptionType"
                        outerLabel="Subscription Type"
                        options={TYPE_OPTIONS}
                        disabled
                        sx={readOnlyFieldSx}
                      />
                      <Box />
                    </Box>

                    <Box style={{ marginTop: 16 }}>
                      {(watched.items || []).map((_item, index) => (
                        <Box
                          key={`item-${index}`}
                          display="grid"
                          gridTemplateColumns="1fr 1fr 40px"
                          style={{ gap: 16, alignItems: 'end', marginBottom: 16 }}>
                          <RHFTextField
                            name={`items.${index}.productName`}
                            outerLabel={index === 0 ? 'Product' : undefined}
                            disabled
                            readOnly
                            sx={readOnlyFieldSx}
                          />
                          <RHFTextField
                            name={`items.${index}.licenses`}
                            outerLabel={index === 0 ? 'Number of Licenses' : undefined}
                            disabled
                            readOnly
                            sx={readOnlyFieldSx}
                          />
                          <Box />
                        </Box>
                      ))}
                    </Box>

                    <Box style={{ marginTop: 16 }}>
                      <RHFTextField
                        name="internalNotes"
                        outerLabel="Internal Notes"
                        placeholder="Enter notes"
                        multiline
                        rows={4}
                      />
                      <Typography
                        style={{
                          fontSize: 12,
                          color: theme.palette.text.secondary,
                          marginTop: 6,
                        }}>
                        (For Internal Use Only): Sales notes, follow-ups, and other relevant
                        information can be documented here.
                      </Typography>
                    </Box>

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      style={{ marginTop: 24 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        color="inherit"
                        disabled={isSubmitting}
                        onClick={handleBack}
                        sx={{ minWidth: 120, height: 42 }}>
                        Back
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        sx={{ minWidth: 120, height: 42, fontWeight: 700 }}>
                        Save
                      </Button>
                    </Box>
                  </Paper>

                  <Paper elevation={0} style={sideCardStyle(divider)}>
                    <Typography
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        marginBottom: 16,
                        color: theme.palette.text.primary,
                      }}>
                      Subscription Preview
                    </Typography>
                    {[
                      {
                        label: 'Customer Reference',
                        value: watched.customerReference?.trim() || '—',
                      },
                      { label: 'Product', value: previewProductLabel },
                      { label: 'Number Of Licenses', value: previewLicenses },
                      { label: 'Client', value: watched.clientName || '—' },
                      { label: 'Created By', value: preview.createdBy },
                      {
                        label: 'Start Date',
                        value: formatDate(preview.startDate || null),
                      },
                      {
                        label: 'End Date',
                        value: formatDate(preview.endDate || null),
                      },
                    ].map((row) => (
                      <Box
                        key={row.label}
                        display="flex"
                        justifyContent="space-between"
                        style={{ gap: 12, marginBottom: 12 }}>
                        <Typography
                          style={{
                            fontSize: 13,
                            color: theme.palette.text.secondary,
                            flexShrink: 0,
                          }}>
                          {row.label}
                        </Typography>
                        <Typography
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            textAlign: 'right',
                          }}>
                          {row.value}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Box>
              </RHFProvider>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default SubscriptionDetails;
